import { Injectable } from "@angular/core";
import { GMarker } from "../../models/gmap/gmarker.model";
import { GPolyline } from "../../models/gmap/gpolyline.model";
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import { C } from "@node_modules/@angular/cdk/keycodes";

@Injectable({
	providedIn: "root",
})
export class GMapsService {
	private _map: google.maps.Map = null;

	private _gmarkers: google.maps.Marker[] = [];
	private _gpolylines: google.maps.Polyline[] = [];

	private _gpolygonOverlays: any[] = [];
	private _infoWindows: google.maps.InfoWindow[] = [];

	private _drawingManager: google.maps.drawing.DrawingManager = null;

	//20.06.2025
	private _markerCluster: MarkerClusterer = null;

	public initMap(centerLatLng: google.maps.LatLng, mapDiv: any, zoom: number = 15) {
		const mapProperties = {
			center: centerLatLng,
			zoom: 15,
			mapTypeId: google.maps.MapTypeId.ROADMAP,
		};

		this._map = new google.maps.Map(mapDiv, mapProperties);

		this.setBoundsZoom(zoom);
	}

	private setBoundsZoom(zoom) {
		google.maps.event.addListenerOnce(this._map, "bounds_changed", function () {
			this.setZoom(Math.min(zoom, this.getZoom()));
		});
	}

	public setClickEventToMap(clickEvent) {
		this._map.addListener("click", function (event) {
			clickEvent(event);
		});
	}

	public MarkerOperation = new (class {
		constructor(public gmapsSuper: GMapsService) {}

		public addMarker(input: GMarker, bounds?: google.maps.LatLngBounds) {
			var markerOptions = {
				position: { lat: input.position.latitude, lng: input.position.longitude },
				map: this.gmapsSuper._map,
				icon: input.icon,
				title: input.title,
				id: input.id,
				label: input.label.text, //.text olarak yazıldı. Çünkü google.maps.Marker string bekliyor
				animation: input.animation,
			};

			this.setBounds(bounds, input.position);

			var marker = new google.maps.Marker(markerOptions);

			this.setMarkerColor(marker, input.color, input.isNumberMarker, input.numberMarkerColor, input.numberMarkerNumber);

			var infoWindowContentString = "";
			const titleHTML = marker.getTitle() 
			? `<div style="margin-bottom: 5px;"><strong>${marker.getTitle()}</strong></div>` : "";

			input.infoWindowContent.forEach((item) => {
				if (item.value.startsWith('http://') || item.value.startsWith('https://') && item.value.trim().length > 10)
				{
					item.value = `<img src="${item.value}" style="width: 60px; height: 60px" />`.toString();
				}
			});

			const windowContentHTML = input.infoWindowContent && input.infoWindowContent.length > 0 
			? input.infoWindowContent.map((kv) => `<div><strong>${kv.key}</strong> ${kv.value}</div>`).join("")
			: "";

			const urlsHTML = input.url && input.url.length > 0
			? '<div style="margin-top: 5px;"><strong>Linkler:</strong><ul style="list-style-type: none; padding-left: 0;">' +
			input.url.map((u) => `<li><a href="${u.link}" target="_blank">${u.title}</a></li>`)
			.join("") + 
			`</ul></div>` : "";
				
			const descriptionHTML = input.description && input.description.length > 0
				? `<div style="margin-top: 5px;"><em>${input.description}</em></div>`
				: "";
			
			infoWindowContentString = `<div style="margin:20px">${titleHTML.toString() + windowContentHTML.toString() + urlsHTML.toString() + descriptionHTML.toString()}</div>`
			
			this.setClickEvent(marker, input.hasClickEvent, infoWindowContentString);

			this.gmapsSuper._gmarkers.push(marker);

			return marker;
		}

		//20.06.2025 MarkerClusters eklendi
		public addMarkers(inputs: GMarker[], useCluster: boolean = false, bounds?: google.maps.LatLngBounds) {
			const createdMarkers: google.maps.Marker[] = [];

			inputs.forEach((input) => {
				const marker = this.addMarker(input, bounds);
				createdMarkers.push(marker);
			});

			if (useCluster) {
				if (this.gmapsSuper._markerCluster) {
					this.gmapsSuper._markerCluster.clearMarkers();
				}
				this.gmapsSuper._markerCluster = new MarkerClusterer({ map: this.gmapsSuper._map, markers: createdMarkers });
			}
		}

		private setMarkerColor(marker: google.maps.Marker, markerColor: string, isMarkerNumber: boolean, numberMarkerColor: string, numberMarkerNumber: number) {		
			const folderMap: { [key: string]: string } = {
				Gray: "map1",
				DarkGray: "map17",
				Green: "map2",
				LightGreen: "map13",
				DarkGreen: "map3",
				Yellow: "map5",
				DarkYellow: "map4",
				Red: "map18",
				BrightRed: "map6",
				Orange: "map7",
				Cyan: "map15",
				Blue: "map14",
				DarkBlue: "map8",
				Purple: "map9",
				DarkPurple: "map11",
				Magenta: "map16",
				LightPink: "map10",
				Pink: "map12",
			};

			const markerColorMap: { [key: string]: string } = {
				Red: "red-dot.png",
				Blue: "blue-dot.png",
				Green: "green-dot.png",
				Yellow: "yellow-dot.png",
				Purple: "purple-dot.png",
				Orange: "orange-dot.png",
				LightBlue: "ltblue-dot.png",
				Pink: "pink-dot.png",
			};

			if (isMarkerNumber && numberMarkerNumber !== undefined && numberMarkerColor) {				
				const folderName = folderMap[numberMarkerColor];
				if (!folderName) {
					alert("Belirtilen renk için number marker bulunamadı.");
					return;
				}

				const iconUrlString = `assets/media/icons/svg/Map/${folderName}/number_${numberMarkerNumber}.png`;
				marker.setIcon(iconUrlString);
				return;
			}

			if (markerColor) {				
				const iconFile = markerColorMap[markerColor];
				if (!iconFile) {
					alert("Belirtilen renk için markerColor bulunamadı.");
					return;
				}

				const iconUrlString = `http://maps.google.com/mapfiles/ms/icons/${iconFile}`;
				marker.setIcon(iconUrlString);
			}
		}

		private setBounds(bounds: google.maps.LatLngBounds, position: any) {
			if (bounds) {
				bounds.extend(position);

				this.gmapsSuper._map.fitBounds(bounds);
			}
		}

		private setClickEvent(marker: google.maps.Marker, hasClickEvent: boolean, infoWindowContent?: string) {
			if (hasClickEvent) {
				let _this = this;

				marker.addListener("click", function () {
					_this.setInfoWindow(marker, infoWindowContent);
				});
			}
		}

		private setInfoWindow(marker: google.maps.Marker, infoWindowContent?: string) {
			var content = infoWindowContent 
			? infoWindowContent 
			: `<div style="margin:20px"><strong>${marker.getTitle()}</strong></div>`.toString();

			const estimatedWidth = (content.length * 8) + 20;
			let infowindow = new google.maps.InfoWindow({
				content: content,
				maxWidth: estimatedWidth,			
			});

			infowindow.open(this.gmapsSuper._map, marker);

			this.gmapsSuper._infoWindows.push(infowindow);
		}

		public deleteMarkers() {
			this.gmapsSuper._gmarkers.forEach((gmarker) => {
				gmarker.setMap(null);
			});
		}

		public deleteMarker(id: number) {
			var marker = this.gmapsSuper._gmarkers.find((x) => x.get("id") == id);

			if (marker) {
				marker.setMap(null);
			}
		}
	})(this);

	public PolygonOperation = new (class {
		constructor(public gmapsSuper: GMapsService) {}

		public generateTool(overlayCompletedEvent, overlayClickEvent) {
			if (this.gmapsSuper._drawingManager) {
				this.gmapsSuper._drawingManager.setMap(null);
			}

			this.gmapsSuper._drawingManager = new google.maps.drawing.DrawingManager({
				drawingMode: google.maps.drawing.OverlayType.POLYGON,
				drawingControl: true,
				drawingControlOptions: {
					position: google.maps.ControlPosition.TOP_CENTER,
					drawingModes: [google.maps.drawing.OverlayType.POLYGON],
				},
				polygonOptions: {
					fillColor: "#1E90FF",
					strokeColor: "#1E90FF",
				},
			});

			this.gmapsSuper._drawingManager.setMap(this.gmapsSuper._map);

			let _this = this;

			this.gmapsSuper._drawingManager.addListener("overlaycomplete", function (polygon) {
				//polygon.overlay.id = 1;

				_this.gmapsSuper._gpolygonOverlays.push(polygon.overlay);

				overlayCompletedEvent(polygon);

				polygon.overlay.addListener("mouseup", function (event) {
					overlayClickEvent(polygon.overlay);
				});
			});
		}

		public deleteOverlays() {
			this.gmapsSuper._gpolygonOverlays.forEach((overlay) => {
				overlay.setMap(null);
			});
		}

		public getOverlayArray(overlay) {
			let arr_lats = [];
			let arr_lngs = [];

			let rt = [];

			let polygonArray = overlay.getPath().getArray();

			polygonArray.map(function (k, v) {
				let lat = k.lat();
				let lng = k.lng();

				rt.push({ lat: lat, lng: lng });

				//arr_lats.push(lat);
				//arr_lngs.push(lng);
			});

			return rt;
		}

		public checkPointInPolygon(point: google.maps.LatLng, polygon: google.maps.Polygon) {
			return google.maps.geometry.poly.containsLocation(point, polygon);
		}

		public changeColor(overlay, newColor = null) {
			if (!newColor) newColor = this.gmapsSuper.HelperOperation.getRandomColor();

			overlay.set("fillColor", newColor);
		}
	})(this);

	public PolylineOperation = new (class {
		constructor(public gmapsSuper: GMapsService) {}

		public generate(input: GPolyline, bounds?: google.maps.LatLngBounds) {
			const _this = this;

			//const flightPlanCoordinates = [
			//    { lat: 37.772, lng: -122.214 },
			//    { lat: 21.291, lng: -157.821 },
			//    { lat: -18.142, lng: 178.431 },
			//    { lat: -27.467, lng: 153.027 },
			//  ];


			const polyline = new google.maps.Polyline({
				path: input.coordinates,
				geodesic: true,
				strokeColor: input.strokeColor ? input.strokeColor : "#1E90FF",
				strokeOpacity: 1.0,
				strokeWeight: input.strokeWeight ? input.strokeWeight : 2,
			});

			this.gmapsSuper._gpolylines.push(polyline);

			if (!bounds) {
				let bounds = new google.maps.LatLngBounds();

				input.coordinates.forEach(function (item) {
					var position = new google.maps.LatLng(item.lat, item.lng);

					_this.gmapsSuper.HelperOperation.setBounds(bounds, position);
				});
			}

			polyline.setMap(this.gmapsSuper._map);
		}

		public deleteAll() {
			this.gmapsSuper._gpolylines.forEach((gpolyline) => {
				gpolyline.setMap(null);
			});
		}

		public delete(id: number) {
			var polyline = this.gmapsSuper._gpolylines.find((x) => x.get("id") == id);

			if (polyline) {
				polyline.setMap(null);
			}
		}
	})(this);

	public HelperOperation = new (class {
		private _color: string[] = ["#1E90FF", "#FF1493", "#32CD32", "#FF8C00", "#4B0082", "#FFFF00", "#808000", "#800000", "#0000FF", "#333333", "#009933", "#16A085", "#CD6155"];

		constructor(public gmapsSuper: GMapsService) {}

		public getRandomColor() {
			var rndColor = Math.floor(Math.random() * 12);

			var newColor = this._color[rndColor];

			var rt = newColor ? newColor : "#298A08";

			return rt;
		}

		public setBounds(bounds: google.maps.LatLngBounds, position: any) {
			if (bounds) {
				bounds.extend(position);

				this.gmapsSuper._map.fitBounds(bounds);
			}
		}
	})(this);
}
